import { useState, useMemo, useCallback } from 'react';
import type { SortingState, PaginationState, RowSelectionState } from '@tanstack/react-table';

import { AlertDialog, Button, Dropdown, Label, toast } from '@heroui/react';
import {
  IconPlus as Plus,
  IconDotsVertical as MoreVertical,
  IconTrash as Trash2,
} from '@tabler/icons-react';
import { trpc, trpcClient } from '@/lib/trpc';
import { useAppSelector } from '@/stores/hooks';
import {
  PageDataGrid,
  PageDataGridActionColumn,
  PageDataGridActionToolbar,
} from '@/components/layouts/page/PageDataGrid';
import { EditorSpaceModal } from './EditorSpaceModal';
import { SpaceAppsSheet } from './components/SpaceAppsSheet';
import { SpaceEntitlementsSheet } from './components/SpaceEntitlementsSheet';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

type Space = {
  id: string;
  name: string;
  code?: string | null;
  type?: string | null;
  isPlatform?: boolean | null;
  enterpriseType?: string | null;
  industry?: string | null;
  status?: string | null;
  isVerified?: boolean | null;
  ownerId?: string | null;
  logoUrl?: string | null;
  createdAt: string;
};

const SPACES_LIST_SORT_FIELDS = new Set([
  'name',
  'type',
  'isPlatform',
  'enterpriseType',
  'industry',
  'status',
]);

function SpacesActionToolbar({
  hasSelection,
  onCreate,
  onDelete,
}: {
  hasSelection: boolean;
  onCreate: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {hasSelection ? (
        <Button variant="danger" onPress={onDelete}>
          <Trash2 className="size-4" stroke={1.5} />
          删除
        </Button>
      ) : null}
      <Button onPress={onCreate}>
        <Plus className="size-4" stroke={1.5} />
        创建
      </Button>
    </div>
  );
}

export default function SpacesManagementPage() {
  const [searchKeyword, setSearchKeyword] = useState('');
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [enumFilters, setEnumFilters] = useState<Record<string, string>>({});

  const [editorModalOpen, setEditorModalOpen] = useState(false);
  const [editingSpace, setEditingSpace] = useState<Space | null>(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [batchDeleteModalOpen, setBatchDeleteModalOpen] = useState(false);
  const [spaceToDelete, setSpaceToDelete] = useState<Space | null>(null);
  const [deletingSpaceId, setDeletingSpaceId] = useState<string | null>(null);
  const [isBatchDeleting, setIsBatchDeleting] = useState(false);

  const [appsModalOpen, setAppsModalOpen] = useState(false);
  const [appsModalSpace, setAppsModalSpace] = useState<Space | null>(null);

  const [entitlementsModalOpen, setEntitlementsModalOpen] = useState(false);
  const [entitlementsModalSpace, setEntitlementsModalSpace] = useState<Space | null>(null);

  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const isHydrated = useAppSelector((state) => state.auth.isHydrated);
  const utils = trpc.useUtils();

  const sortItem = sorting[0];
  const sortBy =
    sortItem && SPACES_LIST_SORT_FIELDS.has(sortItem.id)
      ? (sortItem.id as
          | 'name'
          | 'type'
          | 'isPlatform'
          | 'enterpriseType'
          | 'industry'
          | 'status')
      : undefined;

  const kw = searchKeyword.trim();

  const { data, isLoading } = trpc.spaces.list.useQuery(
    {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      ...(kw ? { name: kw, code: kw } : {}),
      ...(sortBy
        ? {
            sortBy,
            sortOrder: sortItem!.desc ? ('desc' as const) : ('asc' as const),
          }
        : {}),
      ...(enumFilters.type
        ? { type: enumFilters.type as 'PERSONAL' | 'TEAM' | 'ENTERPRISE' }
        : {}),
      ...(enumFilters.isPlatform
        ? { isPlatform: enumFilters.isPlatform === 'true' }
        : {}),
      ...(enumFilters.enterpriseType
        ? {
            enterpriseType: enumFilters.enterpriseType as
              | 'CORP_HEAD'
              | 'SUB_COMPANY'
              | 'BRANCH_ORG',
          }
        : {}),
      ...(enumFilters.industry
        ? {
            industry: enumFilters.industry as
              | 'LIFE_INSURANCE'
              | 'FINANCE'
              | 'INTERNET'
              | 'EDUCATION'
              | 'RETAIL'
              | 'MANUFACTURE'
              | 'CONSTRUCTION'
              | 'MEDICAL'
              | 'REAL_ESTATE'
              | 'LOGISTICS'
              | 'CATERING'
              | 'CULTURE_MEDIA'
              | 'GOVERNMENT'
              | 'SERVICE'
              | 'AGRICULTURE'
              | 'OTHER',
          }
        : {}),
      ...(enumFilters.status
        ? { status: enumFilters.status as 'ACTIVE' | 'SUSPENDED' | 'DISABLED' }
        : {}),
    },
    { enabled: isHydrated && isAuthenticated }
  );

  const spaces = (data?.items ?? []) as Space[];
  const total = data?.total ?? 0;
  const pageCount = useMemo(
    () => Math.ceil(total / (pagination.pageSize || 10)),
    [total, pagination.pageSize]
  );

  const deleteSpaceMutation = trpc.spaces.delete.useMutation({
    onSuccess: () => {
      toast.success('空间删除成功');
      setDeletingSpaceId(null);
      setDeleteModalOpen(false);
      setSpaceToDelete(null);
      void utils.spaces.list.invalidate();
    },
    onError: (error) => {
      toast.danger('删除失败', { description: error.message });
      setDeletingSpaceId(null);
    },
  });

  const handleCreate = () => {
    setEditingSpace(null);
    setEditorModalOpen(true);
  };

  const handleEdit = (space: Space) => {
    setEditingSpace(space);
    setEditorModalOpen(true);
  };

  const handleEditorSuccess = () => {
    setEditorModalOpen(false);
    setEditingSpace(null);
    void utils.spaces.list.invalidate();
  };

  const handleEditorCancel = useCallback(() => {
    setEditorModalOpen(false);
    setEditingSpace(null);
  }, []);

  const handleDelete = (space: Space) => {
    setSpaceToDelete(space);
    setDeleteModalOpen(true);
  };

  const handleManageApps = (space: Space) => {
    setAppsModalSpace(space);
    setAppsModalOpen(true);
  };

  const handleManageEntitlements = (space: Space) => {
    setEntitlementsModalSpace(space);
    setEntitlementsModalOpen(true);
  };

  const handleAppsModalCancel = useCallback(() => {
    setAppsModalOpen(false);
    setAppsModalSpace(null);
  }, []);

  const handleEntitlementsModalCancel = useCallback(() => {
    setEntitlementsModalOpen(false);
    setEntitlementsModalSpace(null);
  }, []);

  const confirmDelete = async () => {
    if (!spaceToDelete) return;
    setDeletingSpaceId(spaceToDelete.id);
    try {
      await deleteSpaceMutation.mutateAsync({ id: spaceToDelete.id });
    } catch {
      /* onError */
    }
  };

  const handleBatchDeleteOpen = () => {
    const selectedKeys = Object.keys(rowSelection).filter((key) => rowSelection[key]);
    if (selectedKeys.length === 0) {
      toast.warning('请先选择要删除的空间');
      return;
    }
    setBatchDeleteModalOpen(true);
  };

  const confirmBatchDelete = async () => {
    const selectedKeys = Object.keys(rowSelection).filter((key) => rowSelection[key]);
    if (selectedKeys.length === 0) return;
    setIsBatchDeleting(true);
    try {
      for (const id of selectedKeys) {
        await trpcClient.spaces.delete.mutate({ id });
      }
      toast.success(`成功删除 ${selectedKeys.length} 个空间`);
      setRowSelection({});
      setBatchDeleteModalOpen(false);
      void utils.spaces.list.invalidate();
    } catch (error: unknown) {
      toast.danger('批量删除失败', {
        description: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setIsBatchDeleting(false);
    }
  };

  const selectedCount = Object.keys(rowSelection).filter((k) => rowSelection[k]).length;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      {appsModalSpace ? (
        <SpaceAppsSheet
          open={appsModalOpen}
          spaceId={appsModalSpace.id}
          spaceName={appsModalSpace.name}
          onCancel={handleAppsModalCancel}
        />
      ) : null}

      {entitlementsModalSpace ? (
        <SpaceEntitlementsSheet
          open={entitlementsModalOpen}
          spaceId={entitlementsModalSpace.id}
          spaceName={entitlementsModalSpace.name}
          onCancel={handleEntitlementsModalCancel}
        />
      ) : null}

      <EditorSpaceModal
        open={editorModalOpen}
        space={editingSpace}
        onCancel={handleEditorCancel}
        onSuccess={handleEditorSuccess}
      />

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <PageDataGrid<Space>
          className="flex min-h-0 min-w-0 flex-1 flex-col"
          source="spaces"
          data={spaces}
          recordCount={total}
          pageCount={pageCount}
          enableSelectColumn
          isLoading={isLoading}
          emptyMessage="暂无数据"
          getRowId={(row) => row.id}
          sorting={sorting}
          onSortingChange={setSorting}
          pagination={pagination}
          onPaginationChange={setPagination}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          searchKeyword={searchKeyword}
          onSearchKeywordChange={setSearchKeyword}
          enumFilters={enumFilters}
          onEnumFiltersChange={setEnumFilters}
          metaQueryOptions={{ enabled: isHydrated && isAuthenticated }}
          paginationBar={{}}
        >
          <PageDataGridActionToolbar>
            <SpacesActionToolbar
              hasSelection={selectedCount > 0}
              onCreate={handleCreate}
              onDelete={handleBatchDeleteOpen}
            />
          </PageDataGridActionToolbar>
          <PageDataGridActionColumn<Space>>
            {({ row }) => {
              const space = row.original;
              return (
                <div className="flex justify-center">
                  <Dropdown>
                    <Button isIconOnly aria-label="操作" size="sm" variant="ghost">
                      <MoreVertical className="size-4" stroke={1.5} />
                    </Button>
                    <Dropdown.Popover className="min-w-[160px]" placement="bottom end">
                      <Dropdown.Menu>
                        <Dropdown.Item
                          id="edit"
                          textValue="编辑"
                          onAction={() => handleEdit(space)}
                        >
                          <Label>编辑</Label>
                        </Dropdown.Item>
                        <Dropdown.Item
                          id="apps"
                          textValue="空间应用管理"
                          onAction={() => handleManageApps(space)}
                        >
                          <Label>空间应用管理</Label>
                        </Dropdown.Item>
                        <Dropdown.Item
                          id="entitlements"
                          textValue="空间权益管理"
                          onAction={() => handleManageEntitlements(space)}
                        >
                          <Label>空间权益管理</Label>
                        </Dropdown.Item>
                        <Dropdown.Item
                          id="delete-row"
                          textValue="删除"
                          variant="danger"
                          onAction={() => handleDelete(space)}
                        >
                          <Label>删除</Label>
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown.Popover>
                  </Dropdown>
                </div>
              );
            }}
          </PageDataGridActionColumn>
        </PageDataGrid>
      </div>

      <AlertDialog isOpen={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <RacDialogTriggerAnchor />
        <AlertDialog.Backdrop>
          <AlertDialog.Container placement="center">
            <AlertDialog.Dialog>
              <AlertDialog.Header>
                <AlertDialog.Heading>确认删除</AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                确定要删除空间「{spaceToDelete?.name}」吗？此操作不可恢复。
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button
                  variant="secondary"
                  onPress={() => {
                    setDeleteModalOpen(false);
                    setSpaceToDelete(null);
                  }}
                >
                  取消
                </Button>
                <Button
                  variant="danger"
                  isDisabled={deletingSpaceId === spaceToDelete?.id}
                  onPress={() => void confirmDelete()}
                >
                  {deletingSpaceId === spaceToDelete?.id ? '删除中…' : '确认删除'}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>

      <AlertDialog isOpen={batchDeleteModalOpen} onOpenChange={setBatchDeleteModalOpen}>
        <RacDialogTriggerAnchor />
        <AlertDialog.Backdrop>
          <AlertDialog.Container placement="center">
            <AlertDialog.Dialog>
              <AlertDialog.Header>
                <AlertDialog.Heading>确认批量删除</AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>确定要删除选中的空间吗？此操作不可恢复。</AlertDialog.Body>
              <AlertDialog.Footer>
                <Button variant="secondary" onPress={() => setBatchDeleteModalOpen(false)}>
                  取消
                </Button>
                <Button
                  variant="danger"
                  isDisabled={isBatchDeleting}
                  onPress={() => void confirmBatchDelete()}
                >
                  {isBatchDeleting ? '删除中…' : '确认删除'}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>
    </div>
  );
}
